import type { CarReport } from '../api/types'
import { formatDate, formatPower, formatYear, vehicleName } from './format'

/**
 * What goes in the `<title>` and the meta tags of one page.
 *
 * **Read this before changing anything here: on a report URL these are set by JavaScript, and half
 * the internet does not run JavaScript.** This is a single-page app behind
 * `navigationFallback`, so every `/kenteken/…` request is served the same static `index.html`.
 * Google renders the page and will see what this writes. Facebook, WhatsApp, LinkedIn, X and Slack
 * fetch the HTML and read it as it came off the disk, so a shared report link previews as whatever
 * `index.html` says, which is the home page's own copy, for every plate.
 *
 * That is a real loss for a page people share, and it is not fixable from here; the fix
 * is a route that renders the shell with these values already in it. The notes in `CLAUDE.md` set
 * out the options. Everything below still earns its place in the meantime, because Google is the
 * traffic that matters most and Google does execute this.
 */

const SITE_NAME = 'AutoChecken'

export interface PageMeta {
  title: string
  description: string
  /** Absolute URL of an image for the social preview, when the page has one worth showing. */
  image?: string
}

/**
 * The report page.
 *
 * **The plate leads.** People search for a plate as a plate, and an exact match at the start of the
 * title is worth more than a tidy brand-first reading.
 *
 * The year is in there because it is what separates two otherwise identical titles for the same
 * model, and because a search result naming the year gets clicked.
 */
export function reportMeta(report: CarReport): PageMeta {
  const name = vehicleName(report.identity.brand, report.identity.tradeName)
  const year = formatYear(report.registration.constructionYear)

  return {
    title: `${report.licensePlate} ${name}${year ? ` (${year})` : ''} | ${SITE_NAME}`,
    description: reportDescription(report),
    // The publisher's press photo of the model, where there is a review. It is the only image the
    // report has, and a preview card with a picture of the car is the whole reason anyone shares
    // one. Note the CSP does not apply to a meta tag; the host only has to be allowed in `img-src`
    // for the picture rendered inside the review card.
    image: report.review?.photoHighResolutionUrl ?? report.review?.photoUrl,
  }
}

/**
 * A description built from fields rather than truncated from the summary paragraph.
 *
 * Search engines cut this at roughly 155 characters, so what matters is which facts come first: the
 * plate, what the car is, and the APK date, which is the single thing most people are checking.
 * Truncating prose would spend that budget on grammar.
 */
function reportDescription(report: CarReport): string {
  const name = vehicleName(report.identity.brand, report.identity.tradeName)
  const year = formatYear(report.registration.constructionYear)
  const power = formatPower(report.powertrain.maximumPowerKw, report.powertrain.maximumPowerHp)
  const apk = formatDate(report.inspection.apkExpiryDate)

  const facts = [year, report.powertrain.energySources?.[0]?.description?.toLowerCase(), power]
    .filter((fact): fact is string => typeof fact === 'string' && fact.length > 0)
    .join(', ')

  const opening = `${report.licensePlate}: ${name}${facts ? `, ${facts}` : ''}.`
  const validity = apk ? ` APK geldig tot ${apk}.` : ''

  return `${opening}${validity} Bekijk het gratis rapport met APK-historie, tellerstand, terugroepacties en techniek.`
}

/** The title for a plate that is being looked up or could not be found. */
export function plateMeta(plate: string, description: string): PageMeta {
  return { title: `${plate} | Kentekencheck | ${SITE_NAME}`, description }
}

export const HOME_META: PageMeta = {
  title: `Gratis kentekencheck | ${SITE_NAME}`,
  description:
    'Voer een Nederlands kenteken in en zie alle RDW-gegevens over de auto in één overzichtelijk rapport: APK, tellerstand, terugroepacties, techniek en milieu.',
}

/** The fill-in checklist, blank or for one car. */
export function checklistMeta(plate?: string, name?: string): PageMeta {
  return plate
    ? {
        title: `Checklist occasion kopen ${plate} | ${SITE_NAME}`,
        description: `Checklist voor het kopen van ${name ? `deze ${name}` : 'deze occasion'} (${plate}): 25 controles, ingevuld met de RDW-gegevens van de auto.`,
      }
    : {
        title: `Checklist occasion kopen | ${SITE_NAME}`,
        description:
          'Gratis checklist voor het kopen van een occasion: 25 controles in vijf fases om af te vinken, aan te vullen en te printen. Met kenteken vullen we de RDW-gegevens van de auto er meteen bij in.',
      }
}

export const NOT_FOUND_META: PageMeta = {
  title: `Pagina niet gevonden | ${SITE_NAME}`,
  description: 'Deze pagina bestaat niet. Voer een Nederlands kenteken in voor een gratis rapport.',
}
